import React from "react";
import { Modal } from "antd";
import { formatMinutesAsHHMM, normalizeMinutes } from "../../../lib/flightTimingUtils";
import { formatSignedDurationDelta } from "../timetable/previewDiffDisplay";

const MIN_TIME_COLOR = "#52c41a";
const EXCEEDED_TIME_COLOR = "#faad14";
const INSUFFICIENT_TIME_COLOR = "#ff4d4f";

let activeTimeBarDetailModal = null;

function openTimeBarDetail({ actualLabel, formattedActual, minLabel, minimum, violation }) {
    activeTimeBarDetailModal?.destroy?.();
    activeTimeBarDetailModal = Modal.info({
        title: actualLabel,
        content: (
            <div className="schedule-draft-rdg-detail-modal-body">
                <div>{`${actualLabel}: ${formattedActual}`}</div>
                <div>{`${minLabel}: ${minimum === null ? "未设置" : formatMinutesAsHHMM(minimum)}`}</div>
                {violation?.message && <div>{violation.message}</div>}
            </div>
        ),
        okText: "关闭",
        maskClosable: true,
        afterClose: () => {
            activeTimeBarDetailModal = null;
        },
    });
}

function buildBarSegments(actualMinutes, minMinutes, hasViolation = false) {
    const actual = normalizeMinutes(actualMinutes);
    const minimum = normalizeMinutes(minMinutes);

    if (actual === null) {
        return [];
    }

    if (hasViolation || (minimum !== null && actual < minimum)) {
        return [{ width: 100, color: INSUFFICIENT_TIME_COLOR }];
    }

    if (!minimum || actual <= 0) {
        return [{ width: 100, color: MIN_TIME_COLOR }];
    }

    const minPercent = Math.min((minimum / actual) * 100, 100);
    return [
        { width: minPercent, color: MIN_TIME_COLOR },
        { width: Math.max(100 - minPercent, 0), color: EXCEEDED_TIME_COLOR },
    ].filter((segment) => segment.width > 0);
}

function getViolationActualMinutes(violation) {
    return normalizeMinutes(violation?.actual_ground_time_minutes ?? violation?.actual_minutes ?? violation?.actual);
}

function getViolationRequiredMinutes(violation) {
    return normalizeMinutes(violation?.required_ground_time_minutes ?? violation?.required_minutes ?? violation?.required);
}

const TimeBarCell = ({
    actualMinutes,
    minMinutes,
    actualLabel = "实际时间",
    minLabel = "最小时间",
    violation = null,
    showViolationTag = false,
    showBar = true,
    previewDiff = null,
}) => {
    const violationActual = getViolationActualMinutes(violation);
    const violationMinimum = getViolationRequiredMinutes(violation);
    const actual = violationActual ?? normalizeMinutes(actualMinutes);
    const previewActual = previewDiff ? normalizeMinutes(previewDiff.after) : null;
    const displayActual = previewActual ?? actual;
    const minimum = violationMinimum ?? normalizeMinutes(minMinutes);
    const previewDeltaLabel = formatSignedDurationDelta(previewDiff?.deltaMinutes);

    if (displayActual === null && !previewDeltaLabel) {
        return <span style={{ display: "block", textAlign: "center" }}>--</span>;
    }

    const formattedActual = displayActual === null ? "--" : formatMinutesAsHHMM(displayActual);
    const isInsufficient = displayActual !== null && minimum !== null && displayActual < minimum;
    const hasViolation = showViolationTag && (Boolean(violation) || isInsufficient);
    const segments = showBar ? buildBarSegments(displayActual, minimum, Boolean(violation)) : [];

    return (
        <div className="schedule-draft-rdg-timebar-cell">
            <button
                type="button"
                className={`schedule-draft-rdg-timebar-trigger${hasViolation ? " schedule-draft-rdg-timebar-trigger-violation" : ""}`}
                aria-label={`${actualLabel}详情`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openTimeBarDetail({ actualLabel, formattedActual, minLabel, minimum, violation });
                }}
            >
                <div style={{ display: "inline-block", minWidth: 56 }}>
                    <div className="schedule-draft-rdg-timebar-value">{formattedActual}</div>
                    {previewDeltaLabel ? <div className="schedule-draft-rdg-preview-diff-delta">{previewDeltaLabel}</div> : null}
                    {showBar && (
                        <div style={{ width: "100%", height: 2, background: "#f0f0f0", marginTop: 2, display: "flex" }}>
                            {segments.map((segment, index) => (
                                <div
                                    key={`${segment.color}-${index}`}
                                    style={{
                                        width: `${segment.width}%`,
                                        height: "100%",
                                        background: segment.color,
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </button>
        </div>
    );
};

export default TimeBarCell;
